import type { ObjectDirective } from 'vue'

export type MailtoTarget = string | { email: string, subject?: string }

/** `mailto:` dla adresu, opcjonalnie z tematem wiadomości. */
export function mailtoHref(target: MailtoTarget): string {
  const { email, subject } = typeof target === 'string' ? { email: target, subject: '' } : target
  return subject ? `mailto:${email}?subject=${encodeURIComponent(subject)}` : `mailto:${email}`
}

function repair(el: HTMLElement, target: MailtoTarget) {
  el.setAttribute('href', mailtoHref(target))
  // Tekst `{{ }}` zwykle naprawia już hydratacja (w miejscu węzła tekstowego
  // zastaje <span> i podmienia węzeł). Span, którego Vue nie śledzi, dostaje
  // adres z wiązania — inaczej użytkownik zobaczyłby „[email protected]".
  const email = typeof target === 'string' ? target : target.email
  el.querySelectorAll('.__cf_email__').forEach(span => span.replaceWith(email))
}

/**
 * Dyrektywa `v-mailto` — link do e-maila odporny na Cloudflare Email Obfuscation.
 *
 * CF przepisuje HTML w locie: `href="mailto:…"` zamienia na
 * `/cdn-cgi/l/email-protection#…`, a adres w treści na `span.__cf_email__`, i
 * dokłada skrypt, który ma to odszyfrować. Ten skrypt blokuje nasz CSP
 * (`script-src` z nonce i 'strict-dynamic' — wstrzyknięty tag nie ma nonce),
 * a hydratacja Vue nie poprawia atrybutów, więc klik w adres otwierał stronę
 * Cloudflare zamiast programu pocztowego. Dotyczy wszystkich marek.
 *
 * SSR renderuje zwykły `href` (`getSSRProps`) — strona bez JS i crawlery widzą
 * to samo co dotąd. Po zamontowaniu dyrektywa ustawia href jeszcze raz, już
 * w przeglądarce, za Cloudflare.
 *
 * Trwałą naprawą byłoby wyłączenie Email Obfuscation w strefach CF — treści
 * CMS wstawianej przez v-html ta dyrektywa nie obejmuje.
 */
export const vMailto: ObjectDirective<HTMLElement, MailtoTarget> = {
  getSSRProps: ({ value }) => ({ href: mailtoHref(value) }),
  mounted: (el, { value }) => repair(el, value),
  updated: (el, { value }) => repair(el, value),
}
